import {
  P2GBox,
  P2GButton,
  P2GDrawer,
  P2GGrid,
  P2GIcon,
  P2GText,
  SvgClose,
  SvgFilter,
  useP2GMediaQueryDown
} from '@parcel2go/core-ui';
import { BasketQuote, QuoteParcel, QuoteExtra } from '~/lib/types/basket-types';
import useDropShopProviders from '~/hooks/use-drop-shop-providers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { appliedQuoteFilters, filterQuotes, QuoteFilterOptions, quoteToAnalyticsEvent } from '~/lib/quotes';
import QuoteFilterContainer from './filter/quote-filter-container';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { CmsPromotion } from './promotions/quote-promotions';
import QuoteResultHeading from './results/quote-results-heading';
import QuotesList from './list';

type QuotesProps = {
  quoteId: string;
  quotes: BasketQuote[];
  postcode?: string;
  defaultFilter?: QuoteFilterOptions;
  onSelect: (index: number, quote: BasketQuote, extras: string[]) => void;
  onPromotionSelect?: (index: number, promotion: CmsPromotion, extras: string[]) => void;
  loading: boolean;
  hideFilter?: boolean;
  disableStickyFilter?: boolean;
  origin?: string;
  destination?: string;
  parcels?: QuoteParcel[];
  originPostcode?: string;
  destinationPostcode?: string;
  onChange?: (
    origin: string,
    destination: string,
    parcels: QuoteParcel[],
    originPostcode?: string,
    destinationPostcode?: string
  ) => void;
  onGeoError?: (error: GeolocationPositionError) => void;
  hideLastUsed?: boolean;
  serviceFilter?: string;
  currentSelectedExtras?: QuoteExtra[];
  enforcedVatMode?: 'inc' | 'exc';
};

export default function Quotes({
  quoteId,
  quotes,
  postcode,
  defaultFilter,
  onSelect,
  onPromotionSelect,
  loading,
  hideFilter,
  disableStickyFilter,
  origin,
  destination,
  onChange,
  onGeoError,
  parcels,
  originPostcode,
  destinationPostcode,
  hideLastUsed,
  serviceFilter,
  currentSelectedExtras,
  enforcedVatMode
}: QuotesProps) {
  const { t } = useTranslation([TranslationNamespaces.QUOTE]);
  const [filter, setFilter] = useState<QuoteFilterOptions>(defaultFilter || { sortType: 'Price' });
  const { track } = useAnalytics();
  const [useGeo, setUseGeo] = useState<boolean>(false);
  const isSmall = useP2GMediaQueryDown('md');
  const [showFilter, setShowFilter] = useState<boolean>(!isSmall);
  const [filterHasChanged, setFilterHasChanged] = useState<boolean>(false);

  const onReset = useCallback(() => {
    setFilter(defaultFilter || { sortType: 'Price' });
    setFilterHasChanged(false);
  }, [defaultFilter]);

  const onChangeFilter = useCallback(
    (filter: QuoteFilterOptions) => {
      setFilter(filter);
      setFilterHasChanged(true);
      track({ type: 'quote_filter_changed', payload: filter });
    },
    [track]
  );

  const onGeolocationError = useCallback(
    (error: GeolocationPositionError) => {
      setUseGeo(false);
      if (onGeoError) {
        onGeoError(error);
      }
    },
    [onGeoError]
  );

  const updatePostcode = useCallback(
    (postcode: string) => {
      onChange?.(origin || '', destination || '', parcels || [], postcode, destinationPostcode);
    },
    [onChange, origin, destination, parcels, destinationPostcode]
  );

  const shops = useDropShopProviders({
    postcode: postcode,
    useGeo: useGeo,
    onGeoError: onGeolocationError,
    isoCode: origin
  });

  const filteredQuotes = useMemo(() => {
    return filterQuotes(quotes, filter, shops.shopProviders, serviceFilter);
  }, [quotes, filter, shops, serviceFilter]);

  useEffect(() => {
    if (enforcedVatMode && filter.incVat !== (enforcedVatMode === 'inc')) {
      onChangeFilter({ ...filter, incVat: enforcedVatMode === 'inc' });
    }
  }, [enforcedVatMode, filter, onChangeFilter]);

  const list = useMemo(() => {
    return (
      <QuotesList
        onReset={onReset}
        filter={filter}
        onSelect={onSelect}
        onPromotionSelect={onPromotionSelect}
        loading={loading}
        origin={origin}
        destination={destination}
        parcels={parcels}
        filteredQuotes={filteredQuotes}
        postcode={postcode}
        shops={shops.shopProviders}
        geo={useGeo}
        onGeoChange={setUseGeo}
        hideLastUsed={hideLastUsed}
        onUpdatePostcode={updatePostcode}
        currentSelectedExtras={currentSelectedExtras}
      />
    );
  }, [
    destination,
    filter,
    filteredQuotes,
    hideLastUsed,
    loading,
    onPromotionSelect,
    onReset,
    onSelect,
    origin,
    parcels,
    postcode,
    shops.shopProviders,
    updatePostcode,
    useGeo,
    currentSelectedExtras
  ]);

  useEffect(() => {
    track({
      type: 'view_item_list',
      payload: {
        item_list_name: quoteId,
        item_list_id: quoteId,
        items: filteredQuotes.map((quote, index) => {
          return { index: index, ...quoteToAnalyticsEvent(quote) };
        })
      },
      persist: false
    });
  }, [filteredQuotes, quoteId]);

  if (hideFilter) {
    return list;
  }

  if (isSmall) {
    return (
      <>
        <P2GBox mb={2}>
          <P2GButton variant="tertiary" onClick={() => setShowFilter((e) => !e)}>
            <P2GIcon palette={filterHasChanged ? 'success' : 'primary'}>
              <SvgFilter />
            </P2GIcon>
            {t('RESULT_CUSTOMISE_FILTER_BUTTON', { defaultValue: 'Customise Results' })}
          </P2GButton>
        </P2GBox>
        {list}
        <P2GDrawer
          open={showFilter}
          onClose={() => setShowFilter(false)}
          closeButtonText="View Results"
          desktopViewOverride="modal"
          modalTitle=""
          anchor="bottom"
        >
          <P2GBox display="flex" justifyContent="space-between" alignItems="center" pb={2}>
            <P2GBox>
              <Trans
                t={t}
                i18nKey="RESULT_FILTER_TITLE"
                defaults={'<0>Filter</0> <1>({{count}} Applied)</1>'}
                values={{ count: appliedQuoteFilters(filter) }}
                components={[<P2GText variant="h2" />, <P2GText variant="body1" />]}
              />
            </P2GBox>
            <P2GIcon size="sm" onClick={() => setShowFilter(false)}>
              <SvgClose />
            </P2GIcon>
          </P2GBox>
          <QuoteFilterContainer
            wrapped={false}
            quotes={quotes}
            filter={filter}
            filteredQuotes={filteredQuotes}
            updateFilter={onChangeFilter}
            origin={origin}
            originPostcode={originPostcode}
            destination={destination}
            destinationPostcode={destinationPostcode}
            parcels={parcels}
            onChange={onChange}
            onReset={filterHasChanged ? onReset : undefined}
            onClose={() => setShowFilter(false)}
            enforcedVatMode={enforcedVatMode}
          />
        </P2GDrawer>
      </>
    );
  }

  return (
    <P2GGrid container direction={'row'} wrap={'nowrap'} spacing={3} key={quoteId}>
      <P2GGrid
        item
        alignSelf={'flex-start'}
        top={disableStickyFilter ? 'initial' : 0}
        position={disableStickyFilter ? 'initial' : 'sticky'}
        maxWidth={350}
        minWidth={275}
      >
        <P2GGrid container direction="column" spacing={3}>
          <P2GGrid item>
            <QuoteResultHeading
              translationKey="RESULT_FILTER_TITLE"
              defaultValue={'<0>Filter</0> <1>({{count}} Applied)</1>'}
              values={{ count: appliedQuoteFilters(filter) }}
              components={[<P2GText variant="h2" />, <P2GText variant="body1" />]}
            />
          </P2GGrid>
          <P2GGrid item>
            <QuoteFilterContainer
              stickyFilterDisabled={disableStickyFilter}
              wrapped={true}
              quotes={quotes}
              filter={filter}
              filteredQuotes={filteredQuotes}
              updateFilter={onChangeFilter}
              origin={origin}
              originPostcode={originPostcode}
              destination={destination}
              destinationPostcode={destinationPostcode}
              parcels={parcels}
              onChange={onChange}
              onReset={filterHasChanged ? onReset : undefined}
              enforcedVatMode={enforcedVatMode}
            />
          </P2GGrid>
        </P2GGrid>
      </P2GGrid>
      <P2GGrid item flexGrow={1}>
        {list}
      </P2GGrid>
    </P2GGrid>
  );
}
